import { memo, useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * PW-12 — the landing's filter bar: segmented Open/Closed with count pills, the search input, and the
 * mine-only toggle.
 *
 * ## Why this is its own memo'd component, and why the raw input value never leaves it
 *
 * CLAUDE.md's input-latency gate: a keystroke must not re-render a data table. The raw `value` lives in
 * THIS component's state and only the DEBOUNCED value crosses the boundary to the parent — so typing
 * re-renders a bar with four controls in it, not a table with a hundred expandable rows below it.
 *
 * Lifting `query` into the tab (the obvious refactor) would undo that silently: nothing would break, the
 * table would just re-render on every character and the page would feel heavy on exactly the picklists that
 * are big enough to need searching.
 */

const DEBOUNCE_MS = 300;

export interface PicklistFilterBarProps {
  tab: 'open' | 'closed';
  onTabChange: (tab: 'open' | 'closed') => void;
  openCount: number;
  /** null while the count is still loading — renders a skeleton rather than a misleading 0. */
  closedCount: number | null;
  /** Fired with the DEBOUNCED value only. */
  onQueryChange: (query: string) => void;
  /** Enter with exactly one match jumps straight to it — the deep link the old dropdown gave up. */
  onSubmitSingleMatch?: () => void;
  singleMatchHint?: string | null;
  /** Rendered only for `issuance.view_all_picklists`; the caller owns that gate. */
  showMineToggle: boolean;
  mineOnly: boolean;
  onMineOnlyChange: (mineOnly: boolean) => void;
}

export const PicklistFilterBar = memo(function PicklistFilterBar({
  tab,
  onTabChange,
  openCount,
  closedCount,
  onQueryChange,
  onSubmitSingleMatch,
  singleMatchHint,
  showMineToggle,
  mineOnly,
  onMineOnlyChange,
}: PicklistFilterBarProps) {
  const [value, setValue] = useState('');
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;

  useEffect(() => {
    const timer = setTimeout(() => onQueryChangeRef.current(value.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);

  // Switching tabs with a query in the box would silently filter the other tab by something the user typed
  // for this one. Clear it, and tell the parent immediately rather than a debounce later.
  const handleTabChange = (next: string) => {
    const tabValue = next === 'closed' ? 'closed' : 'open';
    if (tabValue !== tab) {
      setValue('');
      onQueryChangeRef.current('');
      onTabChange(tabValue);
    }
  };

  const clear = () => {
    setValue('');
    onQueryChangeRef.current('');
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="open" className="gap-2">
              Open
              <CountPill value={openCount} />
            </TabsTrigger>
            <TabsTrigger value="closed" className="gap-2">
              Closed
              <CountPill value={closedCount} />
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && onSubmitSingleMatch) onSubmitSingleMatch();
            }}
            placeholder="Search by PL#, MRS#, or item code…"
            aria-label="Search picklists by PL number, MRS number, or item code"
            className={cn('h-9 pl-8', value && 'pr-8')}
          />
          {value && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Clear filter"
                  onClick={clear}
                  className="absolute right-0 top-1/2 h-7 w-7 -translate-y-1/2 p-0"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Clear filter</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {showMineToggle && (
        <div className="flex items-center gap-2">
          <Switch
            id="picklist-mine-only"
            checked={mineOnly}
            onCheckedChange={onMineOnlyChange}
          />
          <Label htmlFor="picklist-mine-only" className="cursor-pointer text-sm font-normal">
            Show only my picklists
          </Label>
        </div>
      )}

      {singleMatchHint && (
        <p className="w-full text-xs text-muted-foreground">{singleMatchHint}</p>
      )}
    </div>
  );
});

function CountPill({ value }: { value: number | null }) {
  // A skeleton, not a 0. The Closed count is unknown until its own request lands, and a placeholder zero
  // reads as "there are none" — which is exactly the wrong thing to tell someone looking for an old
  // picklist. Neither pill may block first paint.
  if (value === null) return <Skeleton className="h-4 w-6 rounded-full" />;
  return (
    <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
      {value}
    </span>
  );
}
